import { useState, useRef, useEffect } from 'react';
import { dropKitHint } from '../drop-kit-hint.js';
import { api, prepareImageUpload } from '../api.js';
import {
  PARAMS,
  effectiveParam,
  effectiveParams,
  alkPhotoReadToDkh,
  readPhosphateKit,
  writePhosphateKit,
  phosphateForSave,
  phosphateKitProblem,
} from '../params.js';
import { isFreshwaterTank } from '../first-run.js';
import { useConfig } from '../config.js';
import { CoralMark } from './CoralMark.jsx';
import IcpView from './IcpView.jsx';
import { WaterChangeSheet } from './WaterChangeSheet.jsx';
import { DoseLogSheet } from './DoseLogSheet.jsx';
import { CaptureSheet, FollowUpSheet } from './CareView.jsx';
import {
  SALINITY_UNITS,
  readSalinityUnit,
  writeSalinityUnit,
  salinityToPpt,
  isPlausibleSalinity,
  salinityUnitLabel,
  pptToSg,
  formatSalinity,
  sgDecimals,
} from '../salinity.js';
import { confirmPromptFor } from '../reading-confirm.js';
import { implausibleReading } from '../../../shared/reading-plausibility.js';
import { getDisplayUnits, cToF, formatTemp } from '../units.js';
import { photoReadFailedMessage } from '../photo-read.js';
import { localDateString } from '../fill-date.js';
import { logMethodFits } from '../saltwater-tools.js';
import { PHOTO_PARAMS } from '../photo-params.js';
import { isQuarantineTank } from '../../../shared/tank-purpose.js';
import {
  icpSampleTs,
  icpDefaultSelection,
  icpImportPlan,
  icpPanelSavable,
  icpProblemText,
  icpPreviewFigure,
  icpRowHasCard,
  icpRowNotReported,
  icpTargetText,
} from '../icp-import.js';

// Per-method preset metadata (titles, hints, default param) lives in
// app_config `capture_presets.presets` so admin can tune them without
// redeploying. Server stores keys in snake_case (default_param); the
// component reads either casing for resilience to ad-hoc edits.

// The Log tab is the one "+" for anything done at the tank (owner,
// 2026-10-21): a water change, a dose, a note with photos, a dictated note
// or test, a follow-up, and a test. Most were buttons on Home's Today.
const AT_THE_TANK = [
  { k: 'water', title: 'Water change', sub: 'How much, and the salt' },
  { k: 'dose', title: 'Dose', sub: 'Any product: mL, drops, grams or a capful' },
  { k: 'note', title: 'Note or photo', sub: 'What you saw or did, with photos' },
  { k: 'voice', title: 'Say it', sub: 'Dictate a note or a test result' },
  { k: 'followup', title: 'Follow-up', sub: 'Something to check again later' },
];

export function LogView({
  tank,
  onDone,
  onCancel,
  onOpenIcp,
  onOpenImport = null,
  initialMethod = null,
  onSaved = null,
  onOpenHistory = null,
  onOpenJournal = null,
  onOpenTreatments = null,
  onOpenCalculator = null,
}) {
  const [method, setMethod] = useState(initialMethod);
  const [sheet, setSheet] = useState(null); // 'water' | 'dose' | 'voice' | 'followup'
  const [recentDoses, setRecentDoses] = useState([]);
  // A sheet that saved stays on Log and says so: a water change, then a
  // dose, then a test is one routine (review of UI step 3).
  const savedRef = useRef(false);
  const [savedWhat, setSavedWhat] = useState('');
  // An active quarantine or treatment comes first: its log keeps the
  // fish's journey, which a tank note or a plain dose does not.
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    if (!tank?.id || tank.role === 'viewer') return undefined;
    let live = true;
    api(`/api/tanks/${tank.id}/treatments/active`)
      .then((d) => live && setCourses(Array.isArray(d?.courses) ? d.courses : []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tank?.id]);
  const openSheet = (k) => {
    if (k === 'note' && onOpenJournal) return onOpenJournal();
    savedRef.current = false;
    setSavedWhat('');
    setSheet(k);
    if (k === 'dose') {
      api(`/api/tanks/${tank.id}/dose-events?limit=20`)
        .then((rows) => setRecentDoses(Array.isArray(rows) ? rows : []))
        .catch(() => setRecentDoses([]));
    }
  };
  const markSaved = async () => {
    savedRef.current = true;
    await onSaved?.();
  };
  const closeSheet = () => {
    if (savedRef.current) setSavedWhat(AT_THE_TANK.find((t) => t.k === sheet)?.title || '');
    setSheet(null);
  };

  // The server refuses a viewer's every save; the photo flow then read
  // "We couldn't read that photo", which blamed a good photo.
  if (tank?.role === 'viewer') {
    return (
      <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
          LOG A READING
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.5, color: 'var(--text-muted)' }}>
          {tank.name || 'This tank'} is shared with you to view. Only the owner or an editor can log
          readings on this tank. If you help look after it, ask the owner to share it with you as an
          editor.
        </p>
        <button
          onClick={onCancel}
          className="font-heading lv-tap"
          style={{
            minHeight: 44,
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
            borderRadius: 12,
          }}
        >
          Back to the tank
        </button>
      </div>
    );
  }

  if (!method) {
    const methods = [
      {
        k: 'manual',
        title: 'Manual entry',
        sub: 'Type the value yourself · always works',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        ),
      },
      {
        k: 'hanna',
        title: 'Hanna Checker',
        sub: 'Photo of the LCD · Sensei reads the value',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="4" y="3" width="16" height="18" rx="2" />
            <rect x="7" y="7" width="10" height="6" rx="1" />
          </svg>
        ),
      },
      {
        k: 'strip',
        title: 'Colour strip or vial',
        sub: 'Photo vs reference card · colour match',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="5" y="3" width="5" height="18" rx="1" />
            <rect x="14" y="3" width="5" height="12" rx="1" />
          </svg>
        ),
      },
      {
        k: 'refract',
        title: 'Refractometer',
        sub: 'Photo through eyepiece · salinity',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="8" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ),
      },
      {
        k: 'icp',
        title: 'ICP report',
        sub: 'PDF, CSV or XLSX from Triton, ATI, Oceamo, Tropic Marin, Reef Labs (XLSX), Modern Reef',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="15" y2="17" />
          </svg>
        ),
      },
      // Feedback #67: tests kept in a spreadsheet, brought in as history.
      ...(onOpenImport
        ? [
            {
              k: 'import',
              title: 'Past readings from a spreadsheet',
              sub: 'CSV or Excel, or paste from Google Sheets · history only',
              icon: (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="9" y1="4" x2="9" y2="20" />
                </svg>
              ),
            },
          ]
        : []),
    ];
    return (
      <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
          LOG
        </h2>
        {savedWhat && (
          <div className="lv-saved" role="status">
            <span>{savedWhat} saved. Anything else?</span>
            <button type="button" className="ts-chip lv-saved-done" onClick={onCancel}>
              Done
            </button>
          </div>
        )}
        {onOpenTreatments && courses.length > 0 && (
          <ul className="lv-at-tank lv-at-tank--course" aria-label="Quarantine and treatment">
            {courses.map((c) => (
              <li key={c.id}>
                <button type="button" className="lv-at-tank-row" onClick={onOpenTreatments}>
                  <span className="lv-at-tank-title">
                    {c.kind === 'quarantine' ? 'Quarantine log' : 'Treatment log'}: {c.title}
                  </span>
                  <span className="lv-at-tank-sub">
                    Spots, doses, tests and photos, kept with the fish
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <ul className="lv-at-tank" aria-label="At the tank">
          {AT_THE_TANK.map((t) => (
            <li key={t.k}>
              <button type="button" className="lv-at-tank-row" onClick={() => openSheet(t.k)}>
                <span className="lv-at-tank-title">{t.title}</span>
                <span className="lv-at-tank-sub">
                  {t.k === 'water' && isFreshwaterTank(tank) ? 'How much you changed' : t.sub}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <p
          className="font-heading"
          style={{
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
            marginTop: 4,
          }}
        >
          A test: pick your method
        </p>
        {methods
          .filter(
            (t) =>
              logMethodFits(tank, t.k) &&
              (!PHOTO_PARAMS[t.k] || photoParamOptions(tank, t.k).length > 0)
          )
          .map((t) => (
            <button
              key={t.k}
              onClick={() => (t.k === 'import' ? onOpenImport() : setMethod(t.k))}
              className="slide-in"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: 14,
                textAlign: 'left',
                borderRadius: 14,
                background: 'var(--surface)',
                border: '1px solid var(--surface-border)',
                color: 'inherit',
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: 'var(--accent-bg)',
                  color: 'var(--cyan)',
                }}
              >
                {t.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 500 }}>{t.title}</div>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    marginTop: 3,
                    lineHeight: 1.45,
                  }}
                >
                  {t.sub}
                </div>
              </div>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--text-dim)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ))}
        <button
          onClick={onCancel}
          className="font-heading lv-tap"
          style={{
            marginTop: 6,
            padding: '10px 0',
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
            borderRadius: 12,
          }}
        >
          Cancel
        </button>
        {sheet === 'water' && (
          <WaterChangeSheet tank={tank} onClose={closeSheet} onDone={markSaved} />
        )}
        {sheet === 'dose' && (
          <DoseLogSheet
            tank={tank}
            recent={recentDoses}
            onClose={closeSheet}
            onDone={markSaved}
            onOpenCalculator={
              onOpenCalculator && !isFreshwaterTank(tank)
                ? () => {
                    setSheet(null);
                    onOpenCalculator();
                  }
                : null
            }
          />
        )}
        {sheet === 'voice' && (
          <CaptureSheet
            tank={tank}
            onClose={closeSheet}
            onSaved={markSaved}
            onLog={() => setSheet(null)}
          />
        )}
        {sheet === 'followup' && (
          <FollowUpSheet
            tank={tank}
            start="new"
            onClose={closeSheet}
            onSaved={markSaved}
            onHistory={(param) => {
              setSheet(null);
              onOpenHistory?.(param);
            }}
          />
        )}
      </div>
    );
  }

  if (method === 'manual')
    return <ManualLog tank={tank} onDone={onDone} onBack={() => setMethod(null)} />;
  if (method === 'icp')
    return (
      <IcpLog tank={tank} onDone={onDone} onBack={() => setMethod(null)} onOpenIcp={onOpenIcp} />
    );
  return (
    <MockCaptureLog
      tank={tank}
      kind={method}
      onDone={onDone}
      onSaved={onSaved}
      onBack={() => setMethod(null)}
    />
  );
}

// ICP PDF flow: upload → server parses → preview table with checkboxes →
// import the checked rows as readings. Unmappable rows (Iodine, Strontium,
// etc.) are shown in the preview but not selectable for v1.
function IcpLog({ tank, onDone, onBack, onOpenIcp }) {
  const fileRef = useRef(null);
  const [stage, setStage] = useState('pick'); // pick | parsing | preview | saving
  const [preview, setPreview] = useState(null); // server response
  const [selected, setSelected] = useState({}); // { mapped_param: bool }
  const [err, setErr] = useState('');
  const [showOverview, setShowOverview] = useState(false);
  // The picked file is kept so an unreadable report can be offered to us
  // for analysis without making the keeper choose it a second time.
  const [lastFile, setLastFile] = useState(null);
  const [labName, setLabName] = useState('');
  const [submitState, setSubmitState] = useState('idle'); // idle | sending | sent | error
  const [shareLink, setShareLink] = useState('');
  // YYYY-MM-DD. Prefilled from the report; editable because some exports
  // (Triton's CSV) carry no date, and a panel filed under today would sit
  // in the wrong place in the history.
  const [sampleDate, setSampleDate] = useState('');
  // Rows of this report already saved, so a retry after a failure part-way
  // through does not save them twice.
  const savedRowsRef = useRef(new Set());
  // Readings saved, the full panel not: the next tap saves the panel only,
  // at the time the readings were saved under.
  const [panelPending, setPanelPending] = useState(false);
  const pendingTsRef = useRef(null);
  const isPdfFile = (f) => !!f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));

  const showPreview = (body) => {
    setPreview(body);
    savedRowsRef.current = new Set();
    setPanelPending(false);
    setSampleDate(body.sampled_at ? String(body.sampled_at).slice(0, 10) : '');
    // Default-check every mapped row the tank has a card for.
    setSelected(icpDefaultSelection(body.rows, tank));
    setStage('preview');
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr('That file is over 8 MB.');
      return;
    }
    setErr('');
    setLastFile(file);
    setSubmitState('idle');
    setStage('parsing');
    try {
      const r = await fetch(`/api/tanks/${tank.id}/icp/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': isPdfFile(file)
            ? 'application/pdf'
            : /\.xlsx$/i.test(file.name || '')
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : 'text/csv',
        },
        body: file,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Upload failed (${r.status})`);
      showPreview(body);
    } catch (e) {
      setErr(e.message || "Couldn't read that file.");
      setStage('pick');
    }
  };

  const onShareLink = async () => {
    const url = shareLink.trim();
    if (!url) return;
    setErr('');
    setLastFile(null);
    setSubmitState('idle');
    setStage('parsing');
    try {
      const body = await api(`/api/tanks/${tank.id}/icp/preview-link`, {
        method: 'POST',
        body: { url },
      });
      showPreview(body);
    } catch (e) {
      setErr(e.message || "Couldn't read that link.");
      setStage('pick');
    }
  };

  const openOverview = () => (onOpenIcp ? onOpenIcp() : setShowOverview(true));

  const submitUnsupported = async () => {
    if (!lastFile) return;
    setSubmitState('sending');
    try {
      const qs = new URLSearchParams();
      if (labName.trim()) qs.set('lab', labName.trim());
      const r = await fetch(
        `/api/tanks/${tank.id}/icp/submit-lab${qs.toString() ? `?${qs}` : ''}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/pdf' },
          body: lastFile,
        }
      );
      if (!r.ok) throw new Error('send failed');
      setSubmitState('sent');
    } catch {
      setSubmitState('error');
    }
  };

  const importChecked = async () => {
    setStage('saving');
    setErr('');
    // Import waits for a sample date (the button is off without one): a
    // date-less Triton CSV used to be filed as "now", weeks-old water that
    // then fed alerts and the dose calculator as the newest reading.
    if (!sampleDate) {
      setStage('preview');
      setErr('Set the day you took the sample first.');
      return;
    }
    const ts = panelPending ? pendingTsRef.current : icpSampleTs(sampleDate);
    if (!ts) {
      setStage('preview');
      setErr('That sample date is not right. Pick the day you took the sample.');
      return;
    }
    const kit = `${preview.lab || 'ICP'} ICP-OES`;
    // Names this sample for the ICP kit count: one sample takes one kit,
    // however many taps save it.
    const sampleKey = `${preview.lab || 'ICP'}|${sampleDate}`;
    const selectedCount = (preview.rows || []).filter(
      (r) => r.mapped_param && selected[r.mapped_param]
    ).length;
    const plan = panelPending
      ? { toSave: [], problems: [] }
      : icpImportPlan(preview.rows, selected, {
          tank,
          ts,
          sampleDate,
          saved: savedRowsRef.current,
        });
    if (plan.problems.length) {
      setStage('preview');
      setErr(icpProblemText(plan.problems));
      return;
    }
    if (!panelPending && !selectedCount && !icpPanelSavable(preview, tank)) {
      setStage('preview');
      setErr('Tick a reading to import.');
      return;
    }
    try {
      let kitUntracked = false;
      for (const r of plan.toSave) {
        const saved = await api(`/api/tanks/${tank.id}/readings`, {
          method: 'POST',
          body: {
            param: r.param,
            value: r.value,
            unit: r.unit,
            method: 'icp',
            kit,
            ts,
            icp_sample: sampleKey,
            // The keeper set or confirmed this date above; the server
            // refuses an ICP dated within the last day without it.
            confirm_sample_date: true,
            note:
              selectedCount > 1
                ? `From ${preview.lab || 'ICP'} report (${selectedCount} elements)`
                : null,
          },
        });
        savedRowsRef.current.add(r.key);
        if (saved?.icp_kit_untracked) kitUntracked = true;
      }
      // Persist the FULL panel (trace elements + heavy metals, not just
      // the mapped params) so the ICP history overview can band + trend
      // everything. A failure keeps the keeper here with the readings
      // already saved; landing on a results page without this test lost
      // the panel and the PDF, and importing again saved the readings twice.
      let imported;
      try {
        imported = await api(`/api/tanks/${tank.id}/icp/import`, {
          method: 'POST',
          body: {
            lab: preview.lab || null,
            sampled_at: ts,
            rows: preview.rows || [],
            icp_sample: sampleKey,
          },
        });
      } catch (e) {
        if (!plan.toSave.length && !panelPending) throw e;
        // Every reading saved from this report, over every tap.
        const n = savedRowsRef.current.size;
        pendingTsRef.current = ts;
        setPanelPending(true);
        setStage('preview');
        setErr(
          `Your ${n} reading${n === 1 ? ' is' : 's are'} saved. The full ICP panel did not save: tap Save the panel to try again.`
        );
        return;
      }
      // Keep the keeper's own copy of the report with the test. Same file
      // they previewed, sent once the test row exists; a failure here costs
      // the stored PDF, never the import.
      if (imported?.id && isPdfFile(lastFile)) {
        const qs = new URLSearchParams({ name: lastFile.name || 'icp-report.pdf' });
        await fetch(`/api/tanks/${tank.id}/icp/${imported.id}/pdf?${qs}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/pdf' },
          body: lastFile,
        }).catch(() => {});
      }
      // A panel saved alone counts the kit on the server too.
      if (imported?.icp_kit_untracked) kitUntracked = true;
      // Moment-of-use kit capture: the import just consumed a kit the
      // app isn't tracking. Same one-shot memory as the reagent nudge.
      if (kitUntracked && !reagentNudgeDismissed(tank.id, 'icp', 'kit')) {
        setStage('track-kit');
        return;
      }
      // Land on the full-panel overview so the keeper immediately sees the
      // "blood panel" read (trace + metals + trends), not just a saved-toast.
      openOverview();
    } catch (e) {
      setErr(e.message || 'Save failed');
      setStage('preview');
    }
  };

  if (showOverview) {
    return <IcpView tank={tank} onClose={onDone} backLabel="Home" />;
  }

  if (stage === 'track-kit') {
    return <TrackIcpKitNudge tank={tank} lab={preview?.lab || 'ICP'} onDone={openOverview} />;
  }

  const headerRow = (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <div>
        <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
          ICP REPORT
        </h2>
        <p
          className="font-heading"
          style={{
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
            marginTop: 2,
          }}
        >
          {stage === 'preview'
            ? `${preview?.lab || 'Lab'} · ${preview?.rows?.length || 0} elements`
            : 'PDF, CSV, XLSX or ATI link'}
        </p>
      </div>
      <button
        onClick={onBack}
        className="font-heading lv-tap"
        style={{
          fontSize: 12,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          padding: '6px 12px',
          borderRadius: 999,
          color: 'var(--text-muted)',
          border: '1px solid var(--surface-border)',
        }}
      >
        Back
      </button>
    </div>
  );

  // Offered whenever a report yields nothing we can use: an unknown lab,
  // or a layout the row extractor misses. Without this the keeper just
  // gets an error and we never learn the lab exists — their report is the
  // one artefact that lets us add it.
  const unsupportedOffer = (
    <div
      style={{
        borderRadius: 12,
        background: 'var(--surface)',
        border: '1px solid var(--surface-border)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {submitState === 'sent' ? (
        <>
          <strong style={{ fontSize: 15 }}>Thank you — we have it.</strong>
          <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.55, margin: 0 }}>
            Thanks. We'll look at adding this lab. Until then you can log these values by hand.
          </p>
        </>
      ) : (
        <>
          <strong style={{ fontSize: 15 }}>We do not read this lab yet</strong>
          <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.55, margin: 0 }}>
            Send us this report and we will add it. We keep only the text we extract from the PDF,
            which is what we need to teach Sensei the layout — not the file itself.
          </p>
          <input
            className="lv-input"
            value={labName}
            onChange={(e) => setLabName(e.target.value)}
            placeholder="Which lab is it? (optional)"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--surface-border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: 15,
            }}
          />
          <button
            type="button"
            className="lv-tap"
            onClick={submitUnsupported}
            disabled={submitState === 'sending' || !lastFile}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--cyan)',
              color: 'var(--bg)',
              fontWeight: 600,
              fontSize: 15,
              cursor: submitState === 'sending' ? 'default' : 'pointer',
            }}
          >
            {submitState === 'sending' ? 'Sending…' : 'Send this report to TankSensei'}
          </button>
          {submitState === 'error' && (
            <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', margin: 0 }}>
              That did not go through. Try again in a moment.
            </p>
          )}
        </>
      )}
    </div>
  );

  if (stage === 'pick' || stage === 'parsing') {
    return (
      <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {headerRow}
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf,text/csv,.csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onFile}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={stage === 'parsing'}
          className="font-heading"
          style={{
            padding: '40px 20px',
            borderRadius: 14,
            background: 'var(--surface)',
            border: '1px dashed var(--surface-border)',
            color: 'var(--text-muted)',
            fontSize: 13,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            opacity: stage === 'parsing' ? 0.6 : 1,
          }}
        >
          {stage === 'parsing' ? 'Reading your results…' : '+ Choose ICP report'}
        </button>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55 }}>
          Upload your ICP results and we will make them work. Sensei reads each element and shows
          the values; you confirm before saving. CSV and XLSX exports work too (ATI, Triton, Oceamo,
          Tropic Marin, Reef Labs), and for ATI the share link. If we do not recognise your lab yet,
          send us the report and we will add it.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onShareLink();
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <input
            className="lv-input"
            value={shareLink}
            onChange={(e) => setShareLink(e.target.value)}
            placeholder="Paste an ATI share link"
            inputMode="url"
            autoComplete="off"
            aria-label="ATI share link"
            disabled={stage === 'parsing'}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--surface-border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: 15,
            }}
          />
          <button
            type="submit"
            disabled={stage === 'parsing' || !shareLink.trim()}
            className="font-heading lv-tap"
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid var(--surface-border)',
              background: 'transparent',
              color: 'var(--cyan)',
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              opacity: stage === 'parsing' || !shareLink.trim() ? 0.5 : 1,
            }}
          >
            Read link
          </button>
        </form>
        <button
          type="button"
          onClick={openOverview}
          className="font-heading"
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            padding: '8px 14px',
            borderRadius: 999,
            color: 'var(--cyan)',
            border: '1px solid var(--surface-border)',
            background: 'transparent',
          }}
        >
          View full panel &amp; history
        </button>
        {err && (
          <p role="alert" style={{ fontSize: 14, color: 'var(--alert)' }}>
            {err}
          </p>
        )}
        {err && isPdfFile(lastFile) && unsupportedOffer}
      </div>
    );
  }

  // Preview stage
  const rows = preview?.rows || [];
  const previewUnits = {
    salinityUnit: readSalinityUnit(),
    tempUnit: getDisplayUnits(tank).temp,
  };
  const mappedCount = rows.filter((r) => r.mapped_param && selected[r.mapped_param]).length;
  const panelSavable = icpPanelSavable(preview, tank);
  const panelOnly = mappedCount === 0;
  const importOff =
    (panelOnly && !panelSavable && !panelPending) || stage === 'saving' || !sampleDate;
  // Nothing mapped from a lab we do not read is the same dead end as an
  // unreadable PDF, so make the same offer. A lab we read keeps its panel.
  const tickable = rows.some((r) => icpRowHasCard(r, tank));
  const nothingUsable = !tickable && !panelSavable;
  return (
    <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {headerRow}
      <label
        className="font-mono"
        style={{
          fontSize: 13,
          color: 'var(--text-dim)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        Sample date
        <input
          className="lv-input"
          type="date"
          value={sampleDate}
          min="2000-01-01"
          max={localDateString()}
          disabled={panelPending || savedRowsRef.current.size > 0}
          onChange={(e) => setSampleDate(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: 8,
            border: '1px solid var(--surface-border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
          }}
        />
        {!preview?.sampled_at && (
          <span style={{ color: 'var(--alert)' }}>
            not in the file · set the day you took the sample
          </span>
        )}
      </label>
      {nothingUsable && isPdfFile(lastFile) && unsupportedOffer}
      <div
        style={{
          borderRadius: 12,
          background: 'var(--surface)',
          border: '1px solid var(--surface-border)',
          overflow: 'hidden',
        }}
      >
        {rows.map((r, i) => {
          const noCard = !!r.mapped_param && !icpRowHasCard(r, tank);
          const isMapped = !!r.mapped_param && !noCard;
          const checked = isMapped && !!selected[r.mapped_param];
          const meta = isMapped
            ? effectiveParam(tank, r.mapped_param) || PARAMS[r.mapped_param]
            : null;
          const figure = icpPreviewFigure(r, previewUnits);
          return (
            <label
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderTop: i ? '1px solid var(--surface-border)' : 'none',
                opacity: isMapped ? 1 : 0.55,
                cursor: isMapped ? 'pointer' : 'default',
              }}
            >
              <input
                type="checkbox"
                disabled={!isMapped || panelPending}
                checked={checked}
                onChange={() =>
                  isMapped && setSelected((s) => ({ ...s, [r.mapped_param]: !s[r.mapped_param] }))
                }
                style={{ accentColor: 'var(--cyan)', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, color: 'var(--text)' }}>
                  {r.label}
                  {!isMapped && r.duplicate_of && (
                    <span style={{ color: 'var(--text-dim)', marginLeft: 6, fontSize: 13 }}>
                      (dup of {r.duplicate_of})
                    </span>
                  )}
                </div>
                {isMapped && meta && (
                  <div
                    className="font-mono"
                    style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}
                  >
                    → {meta.label} · {icpTargetText(meta, r.mapped_param, previewUnits)}
                  </div>
                )}
                {!isMapped && !r.duplicate_of && (
                  <div
                    className="font-mono"
                    style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}
                  >
                    {noCard
                      ? `no ${(PARAMS[r.mapped_param]?.label || r.label).toLowerCase()} card on this tank · kept in your ICP panel`
                      : r.water === 'ro'
                        ? 'your RO/DI water, not the tank · kept in your ICP panel'
                        : icpRowNotReported(r, tank)
                          ? 'the lab printed no number for it · not a reading'
                          : r.below_detection
                            ? 'below the lab’s detection limit · kept in your ICP panel'
                            : 'kept in your ICP panel, not a daily parameter'}
                  </div>
                )}
              </div>
              <div
                className="font-mono"
                style={{ fontSize: 15, color: 'var(--text)', whiteSpace: 'nowrap' }}
              >
                {icpRowNotReported(r, tank) ? (
                  <span style={{ color: 'var(--text-dim)' }}>not reported</span>
                ) : r.below_detection ? (
                  <span style={{ color: 'var(--text-dim)' }}>not detected</span>
                ) : figure.lab ? (
                  <>
                    <span>{figure.main}</span>
                    <span style={{ color: 'var(--text-dim)', marginLeft: 6, fontSize: 12 }}>
                      (lab: {figure.lab})
                    </span>
                  </>
                ) : (
                  <span>{figure.main}</span>
                )}
              </div>
            </label>
          );
        })}
      </div>
      {err && (
        <p role="alert" style={{ fontSize: 14, color: 'var(--alert)' }}>
          {err}
        </p>
      )}
      {panelOnly && panelSavable && !panelPending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.5, margin: 0 }}>
            {tickable
              ? 'No daily reading ticked. The whole panel goes to your ICP page, and no reading is added to your cards.'
              : 'None of these rows has a card on this tank. The whole panel goes to your ICP page, and no reading is added to your cards.'}
          </p>
          {tickable && (
            <button
              type="button"
              onClick={() => setSelected(icpDefaultSelection(rows, tank))}
              style={{
                alignSelf: 'flex-start',
                minHeight: 44,
                padding: '0 4px',
                background: 'none',
                border: 'none',
                color: 'var(--cyan)',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Tick them all again
            </button>
          )}
        </div>
      )}
      <button
        onClick={importChecked}
        disabled={importOff}
        className="font-heading"
        style={{
          padding: '14px 0',
          borderRadius: 14,
          background: 'var(--cyan)',
          color: 'var(--cyan-text)',
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          opacity: importOff ? 0.5 : 1,
        }}
      >
        {stage === 'saving'
          ? 'Saving…'
          : panelPending
            ? 'Save the panel'
            : panelOnly && panelSavable
              ? 'Save the panel only'
              : `Import ${mappedCount} reading${mappedCount === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

function ManualLog({ tank, onDone, onBack }) {
  const tankParams = effectiveParams(tank);
  const defaultKey = tankParams[0]?.key || 'alk';
  const [param, setParam] = useState(defaultKey);
  const [value, setValue] = useState('');
  const [kit, setKit] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // "Are you sure?" text for a value far outside the keeper's range;
  // saving again with it on screen means "yes, it is right".
  const [ask, setAsk] = useState('');
  // Refractometer users think in S.G. (1.026); pinpoint/probe users
  // think in ppt (35). The toggle below only shows for salinity, and
  // the choice survives via the same localStorage key as the Home
  // quick-add card. Storage is always ppt; this is the input path only.
  const [salinityUnit, setSalinityUnit] = useState(readSalinityUnit);
  const [phosAs, setPhosAs] = useState(readPhosphateKit);
  const p = effectiveParam(tank, param) || PARAMS[param];
  const canSave = value !== '' && !isNaN(Number(value));
  // The HI736 is Hanna's marine checker: a freshwater tank's phosphate is ppm.
  const freshwater = isFreshwaterTank(tank);
  const isHannaP = param === 'phosphate' && !freshwater && phosAs === 'p';
  const isSalinity = param === 'salinity';
  const isSg = isSalinity && salinityUnit === 'sg';
  // Storage is °F; a °C keeper types what their thermometer shows.
  const isCelsius = param === 'temp' && getDisplayUnits(tank).temp === 'C';

  const save = async (confirmed = false) => {
    setBusy(true);
    setErr('');
    const raw = Number(value);
    let outValue = raw;
    if (isSalinity) {
      if (!isPlausibleSalinity(raw, salinityUnit)) {
        setErr(
          isSg
            ? 'S.G. should be between 1.005 and 1.040.'
            : `${salinityUnitLabel(salinityUnit)} should be between 5 and 55 (or an S.G. like 1.026).`
        );
        setBusy(false);
        return;
      }
      // Auto-detects scale mismatches too (S.G.-looking number while
      // ppt/PSU is selected, and vice versa).
      outValue = salinityToPpt(raw, salinityUnit);
    }
    if (isCelsius) outValue = Math.round(cToF(raw) * 10) / 10;
    const kitProblem = phosphateKitProblem(value, isHannaP ? 'p' : 'po4');
    if (kitProblem) {
      setErr(kitProblem);
      setBusy(false);
      return;
    }
    const phos = phosphateForSave(raw, isHannaP ? 'p' : 'po4');
    if (isHannaP) outValue = phos.value;
    const wrong = implausibleReading(param, outValue, {
      subtypeSlug: tank.subtype_slug,
      shown: isCelsius ? `${raw}°C` : null,
    });
    if (wrong) {
      setErr(wrong.message);
      setBusy(false);
      return;
    }
    if (confirmed !== true) {
      const text = confirmPromptFor(tank, param, outValue, { salinityUnit, typed: phos.typed });
      if (text) {
        setAsk(text);
        setBusy(false);
        return;
      }
    }
    setAsk('');
    try {
      await api(`/api/tanks/${tank.id}/readings`, {
        method: 'POST',
        body: {
          param,
          value: outValue,
          unit: p.unit,
          method: 'manual',
          kit: kit.trim() || phos.kit,
          ...(phos.note ? { note: phos.note } : {}),
        },
      });
      onDone();
    } catch (e) {
      setErr(e.message || 'Save failed');
      setBusy(false);
    }
  };

  const pickSalinityUnit = (u) => {
    writeSalinityUnit(u);
    setSalinityUnit(u);
    setValue('');
    setErr('');
    setAsk('');
  };

  const box = {
    padding: 14,
    borderRadius: 12,
    background: 'var(--surface)',
    border: '1px solid var(--surface-border)',
  };
  return (
    <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
            MANUAL ENTRY
          </h2>
          <p
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--cyan)',
              marginTop: 2,
            }}
          >
            Type the value yourself
          </p>
        </div>
        <button
          onClick={onBack}
          className="font-heading lv-tap"
          style={{
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            padding: '6px 12px',
            borderRadius: 999,
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
          }}
        >
          Back
        </button>
      </div>
      <div>
        <div
          className="font-heading"
          style={{
            fontSize: 11,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-dim)',
            marginBottom: 8,
          }}
        >
          Parameter
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {/* The tank's own cards, as on Home: a freshwater cycling tank
              logs ammonia and nitrite, not salinity or calcium. */}
          {(tankParams.length
            ? tankParams
            : Object.entries(PARAMS).map(([key, m]) => ({ key, ...m }))
          ).map(({ key: k, ...meta }) => {
            const active = k === param;
            return (
              <button
                key={k}
                onClick={() => {
                  setParam(k);
                  setAsk('');
                }}
                className="font-heading lv-tap"
                style={{
                  padding: '6px 12px',
                  minHeight: 44,
                  borderRadius: 999,
                  fontSize: 13,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  background: active ? 'var(--cyan)' : 'transparent',
                  color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                  border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={box}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
            gap: 12,
          }}
        >
          <div
            className="font-heading"
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
            }}
          >
            Value{' '}
            {isSalinity
              ? `(${salinityUnitLabel(salinityUnit)})`
              : isCelsius
                ? '(°C)'
                : isHannaP
                  ? '(ppb P)'
                  : p.unit && `(${p.unit})`}
          </div>
          {isSalinity && (
            <div
              role="tablist"
              aria-label="Salinity input unit"
              className="font-heading"
              style={{
                display: 'inline-flex',
                padding: 2,
                borderRadius: 999,
                border: '1px solid var(--surface-border)',
                background: 'var(--surface)',
              }}
            >
              {SALINITY_UNITS.map((u) => {
                const active = salinityUnit === u.k;
                return (
                  <button
                    key={u.k}
                    type="button"
                    role="tab"
                    className="lv-tap"
                    aria-selected={active}
                    onClick={() => pickSalinityUnit(u.k)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: active ? 'var(--cyan)' : 'transparent',
                      color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                      fontSize: 11,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      border: 'none',
                    }}
                  >
                    {u.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {param === 'phosphate' && !freshwater && (
          <PhosphateKitToggle
            value={phosAs}
            onChange={(v) => {
              writePhosphateKit(v);
              setPhosAs(v);
              setValue('');
              setErr('');
              setAsk('');
            }}
          />
        )}
        <input
          className="lv-input"
          type="number"
          inputMode="decimal"
          enterKeyHint="done"
          step={isSg ? '0.001' : '0.01'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setAsk('');
          }}
          onKeyDown={(e) => {
            // Enter/Done on keyboards that have one saves directly —
            // matching the Home quick-add (user-reported friction).
            if (e.key === 'Enter' && canSave && !busy) {
              e.preventDefault();
              save(Boolean(ask));
            }
          }}
          placeholder={
            isSg
              ? '1.026'
              : isCelsius
                ? formatTemp(p.target, 'C', { withUnit: false })
                : isHannaP || p.target == null
                  ? ''
                  : String(p.target)
          }
          autoFocus
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '4px 0',
            fontSize: 31,
            color: 'var(--text)',
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontWeight: 500,
          }}
        />
        {isHannaP && (
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
            <PhosphateSaved value={value} />
          </div>
        )}
        <div className="font-mono" style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
          {(() => {
            // Custom params can be saved without an acceptable band —
            // dereferencing [0]/[1] unguarded white-screened Manual
            // entry the moment such a param's chip was tapped.
            const band =
              Array.isArray(p.acceptable) && p.acceptable.length === 2 ? p.acceptable : null;
            if (isSg) {
              // Band edges at 4 decimals: the default ±0.3 ppt band is
              // ≈±0.0002 S.G., which collapses to "1.026–1.026" at the
              // input's native 3 decimals.
              return (
                <>
                  target {pptToSg(p.target)} S.G.
                  {band && (
                    <>
                      {' '}
                      · acceptable {pptToSg(band[0], 4)}–{pptToSg(band[1], 4)}
                    </>
                  )}
                </>
              );
            }
            if (isCelsius) {
              return (
                <>
                  target {formatTemp(p.target, 'C')}
                  {band && (
                    <>
                      {' '}
                      · acceptable {formatTemp(band[0], 'C', { withUnit: false })}–
                      {formatTemp(band[1], 'C')}
                    </>
                  )}
                </>
              );
            }
            return (
              <>
                target {p.target ?? '—'}
                {isSalinity ? ` ${salinityUnitLabel(salinityUnit)}` : p.unit && ` ${p.unit}`}
                {band && (
                  <>
                    {' '}
                    · acceptable {band[0]}–{band[1]}
                  </>
                )}
              </>
            );
          })()}
        </div>
        {dropKitHint(param, kit) && (
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.45 }}>
            {dropKitHint(param, kit)}
          </div>
        )}
      </div>
      <div style={box}>
        <div
          className="font-heading"
          style={{
            fontSize: 11,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-dim)',
            marginBottom: 4,
          }}
        >
          Test kit <span style={{ color: 'var(--text-dim)' }}>(optional)</span>
        </div>
        <input
          className="lv-input"
          value={kit}
          onChange={(e) => setKit(e.target.value)}
          placeholder="e.g. Salifert, Red Sea, Hanna HI772"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '4px 0',
            fontSize: 16,
            color: 'var(--text)',
          }}
        />
      </div>
      {err && (
        <p role="alert" style={{ fontSize: 14, color: 'var(--alert)' }}>
          {err}
        </p>
      )}
      {ask && (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid rgba(255, 193, 102, 0.55)',
            background: 'rgba(255, 193, 102, 0.08)',
          }}
        >
          <p style={{ fontSize: 15, margin: 0, lineHeight: 1.5 }}>{ask}</p>
          <button
            type="button"
            onClick={() => save(true)}
            disabled={busy}
            className="ts-chip"
            style={{ marginTop: 8, minHeight: 44, padding: '0 18px' }}
          >
            Yes, save it
          </button>
        </div>
      )}
      <button
        onClick={() => save(Boolean(ask))}
        disabled={!canSave || busy}
        className="font-heading lv-tap"
        style={{
          padding: '14px 0',
          borderRadius: 14,
          background: 'var(--cyan)',
          color: 'var(--cyan-text)',
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          opacity: !canSave || busy ? 0.5 : 1,
        }}
      >
        {busy ? 'Saving…' : 'Log this reading'}
      </button>
    </div>
  );
}

// What each photo method reads (photo-params.js), kept to the tank's own
// cards: a community tank was offered alkalinity and calcium (hidden there),
// and a reading saved to a hidden or deleted card never showed on Home. Like
// every photo read the number is filled in for the keeper to check and save.
// The chip picked first: ammonia on a cycling tank or a QT, where it is the
// test being photographed, else the preset's default if the tank has it.
export function firstPhotoParam(tank, options, preferred) {
  if (options.includes('ammonia') && (tank?.phase === 'cycling' || isQuarantineTank(tank)))
    return 'ammonia';
  return options.length && !options.includes(preferred) ? options[0] : preferred;
}

export function photoParamOptions(tank, kind) {
  const list = PHOTO_PARAMS[kind] || [];
  const cards = new Set(effectiveParams(tank).map((c) => c.key));
  return list.filter((k) => cards.has(k));
}

// Phosphate (PO₄, ppm) or Hanna Phosphorus (HI736, ppb P), the same two
// choices Home's quick add offers; the choice is kept on this phone.
function PhosphateKitToggle({ value, onChange, disabled = false }) {
  return (
    <div>
      <div
        className="font-heading"
        role="tablist"
        aria-label="Phosphate test type"
        style={{
          display: 'inline-flex',
          padding: 2,
          borderRadius: 999,
          border: '1px solid var(--surface-border)',
          background: 'var(--surface)',
        }}
      >
        {[
          { k: 'po4', label: 'Phosphate ppm' },
          { k: 'p', label: 'Hanna P ppb' },
        ].map((u) => {
          const active = value === u.k;
          return (
            <button
              key={u.k}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => onChange(u.k)}
              className="lv-tap"
              style={{
                minHeight: 44,
                padding: '4px 12px',
                borderRadius: 999,
                background: active ? 'var(--cyan)' : 'transparent',
                color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                border: 'none',
              }}
            >
              {u.label}
            </button>
          );
        })}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.45 }}>
        {value === 'p'
          ? 'Type the whole number the Hanna Phosphorus checker (HI736) shows, in ppb. It is saved as phosphate (PO₄) in ppm.'
          : 'Phosphate in ppm, as most kits and the Hanna HI713 show it. Using the Hanna Phosphorus checker? Pick Hanna P.'}
      </p>
    </div>
  );
}

// What a Hanna P number will be saved as, under the field, so a wrong
// choice shows before the save.
function PhosphateSaved({ value }) {
  const n = Number(value);
  if (value === '' || !Number.isFinite(n)) return 'Phosphorus in ppb, saved as phosphate in ppm.';
  const ppm = phosphateForSave(n, 'p').value;
  return `${value} ppb phosphorus: ${Math.round(ppm * 1000) / 1000} ppm phosphate will be saved.`;
}

function MockCaptureLog({ tank, kind, onDone, onSaved = null, onBack }) {
  const presets = useConfig()?.capture?.presets || {};
  const presetRaw = presets[kind] || {};
  // Tolerate either snake_case (server) or camelCase (legacy) defaults.
  const preset = {
    title: presetRaw.title || kind,
    subtitle: presetRaw.subtitle || '',
    defaultParam: presetRaw.default_param || presetRaw.defaultParam || 'alk',
    confidence: presetRaw.confidence ?? 90,
    hint: presetRaw.hint || '',
  };
  const [stage, setStage] = useState('intro');
  const options = photoParamOptions(tank, kind);
  const [param, setParam] = useState(() => firstPhotoParam(tank, options, preset.defaultParam));
  // Which phosphate checker: Hanna HI736 shows phosphorus in ppb.
  const [phosAs, setPhosAs] = useState(readPhosphateKit);
  const freshwater = isFreshwaterTank(tank);
  const isHannaP = kind === 'hanna' && param === 'phosphate' && !freshwater && phosAs === 'p';
  // A reply belongs to the photo it was read from: a chip tapped while
  // Sensei was reading put a phosphate read on Nitrate.
  const readSeq = useRef(0);
  // Never the target: a number in this field is one tap from being saved
  // as measured.
  const [value, setValue] = useState('');
  // The photo could not be read: the field is empty and the keeper types.
  const [readFailed, setReadFailed] = useState(false);
  // "Are you sure?" text for a value far outside the keeper's range.
  const [ask, setAsk] = useState('');
  const salinityUnit = readSalinityUnit();
  const valueRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null); // object URL of captured photo
  const [confidence, setConfidence] = useState(null); // 0..1 from Claude Vision, or null
  const [note, setNote] = useState(''); // Sensei's short comment about the read
  // A salinity read as the field showed it and in ppt: on S.G. the field
  // rounds to 3 decimals, and 36.9 ppt shown as 1.027 would save as 36.3.
  const [salRead, setSalRead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  // After a failed read the number field takes focus once it is on screen;
  // a timeout fired before React had rendered it.
  useEffect(() => {
    if (stage === 'result' && readFailed) valueRef.current?.focus();
  }, [stage, readFailed]);
  const p = effectiveParam(tank, kind === 'refract' ? 'salinity' : param) || PARAMS[param];
  const shownUnit =
    (kind === 'refract' ? 'salinity' : param) === 'salinity'
      ? salinityUnitLabel(salinityUnit)
      : isHannaP
        ? 'ppb P'
        : p.unit;
  // 4 decimals for an S.G. band: a tight ppt band collapses at 3.
  const sgBandDecimals = salinityUnit === 'sg' ? 4 : undefined;

  // One hidden input serves both paths: `capture` forces the camera on
  // phones, removing it lets the OS offer the photo library. Toggled
  // per-tap so the user can pick either.
  const openCamera = () => {
    fileRef.current?.setAttribute('capture', 'environment');
    fileRef.current?.click();
  };
  const openGallery = () => {
    fileRef.current?.removeAttribute('capture');
    fileRef.current?.click();
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr('');
    setAsk('');
    setReadFailed(false);
    setConfidence(null);
    setNote('');
    setSalRead(null);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    const url = URL.createObjectURL(file);
    setPhotoUrl(url);
    setStage('analyzing');
    const seq = ++readSeq.current;

    try {
      // Canvas-first for every format (Safari decodes HEIC natively,
      // so iPhones ship a small JPEG); raw bytes only when this
      // browser can't decode — server normalises those.
      const { body: uploadBody, mime } = await prepareImageUpload(file, {
        maxDim: 1200,
        quality: 0.85,
      });
      const analyzeKind = kind === 'refract' ? 'refract' : kind; // passed via query
      const qs = new URLSearchParams({
        method: analyzeKind,
        param: kind === 'refract' ? 'salinity' : param,
        // Else the read is asked for "Phosphate (ppm)" and may convert itself.
        ...(isHannaP ? { kit: 'Hanna Phosphorus HI736, shows ppb' } : {}),
      }).toString();
      const res = await fetch(`/api/tanks/${tank.id}/readings/analyze?${qs}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mime },
        body: uploadBody,
      });
      const body = await res.json().catch(() => null);
      if (seq !== readSeq.current) return;
      if (!res.ok) {
        throw Object.assign(new Error('photo read failed'), {
          code: body?.code || null,
          status: res.status,
          detail: typeof body?.error === 'string' ? body.error : null,
        });
      }
      const read = Number(body?.value);
      if (!Number.isFinite(read)) {
        throw Object.assign(new Error('photo read failed'), { code: 'bad_response' });
      }
      // Accept the number. Keep confidence for the UI. User still verifies.
      // Refractometers carry both scales (ppt and S.G.) and Vision may
      // read either column. Normalise salinity to canonical ppt — an
      // S.G.-scale read like 1.026 auto-converts to 35.0 — so the saved
      // reading is always in storage units regardless of which scale
      // the photo showed. The field then shows it in the keeper's own
      // scale; save converts back, detection-first.
      const isSalinityRead = (kind === 'refract' ? 'salinity' : param) === 'salinity';
      // A ppm alk checker's 143 is 8.0 dKH, as on Home's quick add.
      const alk =
        !isSalinityRead && param === 'alk' ? alkPhotoReadToDkh(read, body.unit, kind) : null;
      if (isSalinityRead) {
        const ppt = salinityToPpt(read, 'ppt');
        // At the places the card shows: 36.9 ppt is 1.0274 on S.G., not a
        // 1.027 that reads inside the band.
        const shown = String(
          formatSalinity(
            ppt,
            salinityUnit,
            salinityUnit === 'sg' ? sgDecimals(p?.acceptable, ppt) : undefined
          )
        );
        setSalRead({ shown, ppt });
        setValue(shown);
      } else {
        setValue(String(alk ? alk.value : read));
      }
      // The checker said ppb: it is the phosphorus checker (HI736).
      if (
        kind === 'hanna' &&
        param === 'phosphate' &&
        !freshwater &&
        /ppb|µg|ug/i.test(String(body.unit || ''))
      ) {
        setPhosAs('p');
      }
      setConfidence(typeof body.confidence === 'number' ? body.confidence : null);
      setNote([body.note || '', alk?.note || ''].filter(Boolean).join(' · '));
      setStage('result');
    } catch (err) {
      if (seq !== readSeq.current) return;
      // The keeper types the number; nothing is filled in for them.
      setValue('');
      setConfidence(null);
      setReadFailed(true);
      setErr(
        photoReadFailedMessage({ code: err?.code, status: err?.status, kind, detail: err?.detail })
      );
      setStage('result');
    }
  };

  const retake = () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null);
    setConfidence(null);
    setNote('');
    setSalRead(null);
    setErr('');
    setAsk('');
    setReadFailed(false);
    setStage('intro');
  };

  // A refractometer reads salinity and nothing else, so its value must not
  // land on another param chip.
  const saveParam = kind === 'refract' ? 'salinity' : param;
  const isSalinitySave = saveParam === 'salinity';
  const canSave = value !== '' && Number.isFinite(Number(value));

  const save = async (confirmed = false) => {
    if (!canSave) return;
    setBusy(true);
    setErr('');
    const raw = Number(value);
    let outValue = raw;
    if (isSalinitySave) {
      if (!isPlausibleSalinity(raw, salinityUnit)) {
        setErr(
          salinityUnit === 'sg'
            ? 'S.G. should be between 1.005 and 1.040.'
            : `${salinityUnitLabel(salinityUnit)} should be between 5 and 55 (or an S.G. like 1.026).`
        );
        setBusy(false);
        return;
      }
      outValue =
        salRead && value === salRead.shown ? salRead.ppt : salinityToPpt(raw, salinityUnit);
    }
    const kitProblem = phosphateKitProblem(value, isHannaP ? 'p' : 'po4');
    if (kitProblem) {
      setErr(kitProblem);
      setBusy(false);
      return;
    }
    const phos = phosphateForSave(raw, isHannaP ? 'p' : 'po4');
    if (isHannaP) outValue = phos.value;
    // The same checks a typed reading gets: a number no kit can read is
    // refused, one far outside the keeper's range is asked about.
    const wrong = implausibleReading(saveParam, outValue, { subtypeSlug: tank.subtype_slug });
    if (wrong) {
      setErr(wrong.message);
      setBusy(false);
      return;
    }
    if (confirmed !== true) {
      const text = confirmPromptFor(tank, saveParam, outValue, {
        salinityUnit,
        typed: phos.typed,
      });
      if (text) {
        setAsk(text);
        setBusy(false);
        return;
      }
    }
    setAsk('');
    try {
      const saved = await api(`/api/tanks/${tank.id}/readings`, {
        method: 'POST',
        body: {
          param: saveParam,
          value: outValue,
          unit: isSalinitySave ? 'ppt' : p.unit,
          method: kind,
          // Real confidence from Claude Vision if we got one; omitted otherwise.
          confidence: confidence != null ? Math.round(confidence * 100) : null,
          note: [note, phos.note].filter(Boolean).join(' · ') || null,
          ...(phos.kit ? { kit: phos.kit } : {}),
        },
      });
      // Moment-of-use reagent capture: this check burned a consumable
      // the app isn't tracking. Offer once per tank+method+param — a
      // dismissed nudge stays dismissed (localStorage), never a nag.
      if (saved?.reagent_untracked && !reagentNudgeDismissed(tank.id, kind, param)) {
        // The reading is saved: Home shows it even if the keeper leaves by a
        // tab instead of answering the question.
        Promise.resolve(onSaved?.()).catch(() => {});
        setBusy(false);
        setStage('track-reagent');
        return;
      }
      onDone();
    } catch (e) {
      setErr(e.message || 'Save failed');
      setBusy(false);
    }
  };

  if (stage === 'track-reagent') {
    return (
      <TrackReagentNudge
        tank={tank}
        method={kind}
        param={param}
        paramLabel={p.label}
        onDone={onDone}
      />
    );
  }

  return (
    <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
            {preset.title.toUpperCase()}
          </h2>
          <p
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--cyan)',
              marginTop: 2,
            }}
          >
            {preset.subtitle}
          </p>
        </div>
        <button
          onClick={onBack}
          className="font-heading lv-tap"
          style={{
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            padding: '6px 12px',
            borderRadius: 999,
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
          }}
        >
          Back
        </button>
      </div>

      {options.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {options.map((k) => {
            const active = k === param;
            const meta = effectiveParam(tank, k) || PARAMS[k] || { label: k, target: 0 };
            return (
              <button
                key={k}
                disabled={stage === 'analyzing'}
                onClick={() => {
                  readSeq.current += 1;
                  setParam(k);
                  setValue('');
                  setErr('');
                  setAsk('');
                  setReadFailed(false);
                  setStage('intro');
                }}
                className="font-heading lv-tap"
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 13,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  background: active ? 'var(--cyan)' : 'transparent',
                  color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                  border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      )}
      {kind === 'hanna' && param === 'phosphate' && !freshwater && (
        <PhosphateKitToggle
          value={phosAs}
          disabled={stage === 'analyzing'}
          onChange={(v) => {
            writePhosphateKit(v);
            setPhosAs(v);
            setAsk('');
          }}
        />
      )}
      {/* A strip or vial photo of alk / calcium / magnesium is often a
        drop kit's tube, whose colour midway is not the answer (#64). */}
      {kind === 'strip' && dropKitHint(param) && (
        <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          {dropKitHint(param)}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={onPhoto}
        style={{ display: 'none' }}
      />

      <div
        style={{
          position: 'relative',
          aspectRatio: '4 / 3',
          borderRadius: 16,
          overflow: 'hidden',
          background: photoUrl ? '#000' : 'var(--viewfinder-bg)',
          border: '1px solid var(--surface-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {photoUrl && (
          <img
            src={photoUrl}
            alt="Captured test"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: stage === 'analyzing' ? 0.55 : 1,
              transition: 'opacity 0.25s',
            }}
          />
        )}
        {[
          [0, 0],
          [1, 0],
          [0, 1],
          [1, 1],
        ].map(([x, y]) => (
          <div
            key={`${x}${y}`}
            style={{
              position: 'absolute',
              width: 20,
              height: 20,
              top: y === 0 ? 12 : 'auto',
              bottom: y === 1 ? 12 : 'auto',
              left: x === 0 ? 12 : 'auto',
              right: x === 1 ? 12 : 'auto',
              borderTop: y === 0 ? '2px solid var(--cyan)' : 'none',
              borderBottom: y === 1 ? '2px solid var(--cyan)' : 'none',
              borderLeft: x === 0 ? '2px solid var(--cyan)' : 'none',
              borderRight: x === 1 ? '2px solid var(--cyan)' : 'none',
            }}
          />
        ))}
        {stage === 'intro' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <CoralMark size={34} style={{ marginBottom: 8 }} />
            <div
              className="font-heading"
              style={{
                fontSize: 12,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              {preset.hint}
            </div>
          </div>
        )}
        {stage === 'analyzing' && (
          <div className="sensei-reading" style={{ textAlign: 'center' }}>
            <CoralMark className="sensei-pulse" size={34} />
            <div
              className="font-heading"
              style={{
                fontSize: 13,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--cyan)',
                marginTop: 10,
              }}
            >
              Sensei is reading…
            </div>
          </div>
        )}
        {stage === 'result' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div
              className="font-mono"
              style={{ fontSize: 50.5, color: 'var(--text)', fontWeight: 500, lineHeight: 1 }}
            >
              {value === '' ? '—' : value}
              <span style={{ fontSize: 18, color: 'var(--text-muted)', marginLeft: 6 }}>
                {shownUnit}
              </span>
            </div>
            <div
              className="font-heading"
              style={{
                fontSize: 11,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: confidence != null && confidence < 0.6 ? 'var(--alert)' : 'var(--cyan)',
                marginTop: 10,
              }}
            >
              {readFailed
                ? 'Not read · type the number'
                : confidence != null
                  ? `${Math.round(confidence * 100)}% confident · please verify`
                  : 'Please verify · enter manually if wrong'}
            </div>
          </div>
        )}
      </div>

      {stage === 'intro' && (
        <>
          <button
            onClick={openCamera}
            className="font-heading lv-tap"
            style={{
              padding: '14px 0',
              borderRadius: 14,
              background: 'var(--cyan)',
              color: 'var(--cyan-text)',
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Take photo
          </button>
          <button
            onClick={openGallery}
            className="font-heading lv-tap"
            style={{
              padding: '12px 0',
              borderRadius: 14,
              background: 'transparent',
              color: 'var(--cyan)',
              fontWeight: 500,
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              border: '1px solid var(--accent-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Choose from gallery
          </button>
        </>
      )}

      {stage === 'result' && (
        <>
          {note && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 12,
                background: 'var(--accent-bg)',
                border: '1px solid var(--accent-border)',
              }}
            >
              <CoralMark size={18} style={{ flexShrink: 0 }} />
              <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text)' }}>{note}</p>
            </div>
          )}
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: 'var(--surface)',
              border: '1px solid var(--surface-border)',
            }}
          >
            <div
              className="font-heading"
              style={{
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--text-dim)',
                marginBottom: 4,
              }}
            >
              {readFailed ? `Your reading (${shownUnit})` : 'Nudge if needed'}
            </div>
            <input
              ref={valueRef}
              className="lv-input"
              type="number"
              inputMode="decimal"
              enterKeyHint="done"
              step="any"
              value={value}
              placeholder={readFailed ? 'Type it here' : undefined}
              aria-label={`${p.label} reading`}
              onChange={(e) => {
                setValue(e.target.value);
                setAsk('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !busy && canSave) {
                  e.preventDefault();
                  save(Boolean(ask));
                }
              }}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '4px 0',
                fontSize: 24,
                color: 'var(--text)',
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontWeight: 500,
              }}
            />
            <div
              className="font-mono"
              style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}
            >
              {isSalinitySave ? (
                <>
                  target {formatSalinity(p.target, salinityUnit)} {shownUnit} · acceptable{' '}
                  {formatSalinity(p.acceptable[0], salinityUnit, sgBandDecimals)}–
                  {formatSalinity(p.acceptable[1], salinityUnit, sgBandDecimals)}
                </>
              ) : isHannaP ? (
                <PhosphateSaved value={value} />
              ) : (
                <>
                  target {p.target}
                  {p.unit && ` ${p.unit}`} · acceptable {p.acceptable[0]}–{p.acceptable[1]}
                </>
              )}
            </div>
          </div>
          {err && (
            <p role="alert" style={{ fontSize: 15, color: 'var(--alert)', lineHeight: 1.5 }}>
              {err}
            </p>
          )}
          {ask && (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(255, 193, 102, 0.55)',
                background: 'rgba(255, 193, 102, 0.08)',
              }}
            >
              <p style={{ fontSize: 16, margin: 0, lineHeight: 1.5 }}>{ask}</p>
              <button
                type="button"
                onClick={() => save(true)}
                disabled={busy}
                className="ts-chip"
                style={{ marginTop: 8, minHeight: 44, padding: '0 18px' }}
              >
                Yes, save it
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => save(Boolean(ask))}
              disabled={busy || !canSave}
              className="font-heading"
              style={{
                flex: 1,
                padding: '12px 0',
                borderRadius: 12,
                background: 'var(--cyan)',
                color: 'var(--cyan-text)',
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                minHeight: 44,
                opacity: busy || !canSave ? 0.5 : 1,
              }}
            >
              {busy ? 'Saving…' : readFailed ? 'Log this reading' : 'Looks right · Log it'}
            </button>
            <button
              onClick={retake}
              className="font-heading lv-tap"
              style={{
                padding: '12px 16px',
                borderRadius: 12,
                color: 'var(--text-muted)',
                border: '1px solid var(--surface-border)',
                fontSize: 14,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              Retake
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// One-shot memory for the reagent nudge — "Not now" means not again
// for this tank+method+param. A nag here would poison the trust that
// makes the whole supplies loop work.
function reagentNudgeKey(tankId, method, param) {
  return `aqs.reagentNudge.${tankId}.${method}.${param}`;
}
function reagentNudgeDismissed(tankId, method, param) {
  try {
    return localStorage.getItem(reagentNudgeKey(tankId, method, param)) === '1';
  } catch {
    return false;
  }
}

// Moment-of-use reagent capture: the reading just saved burned a
// consumable the app isn't tracking. Everything is prefilled from the
// admin-editable test_consumables spec; the keeper confirms package
// size and taps once. From then on every check draws the box down
// automatically.
function TrackReagentNudge({ tank, method, param, paramLabel, onDone }) {
  const specs = useConfig()?.test_consumables?.specs || {};
  const spec = specs[method]?.[param] || specs[method]?._default || null;
  const [size, setSize] = useState(spec?.size != null ? String(spec.size) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const defaultName = spec?.label || `${paramLabel || param} test refills`;
  // Strips serve every param on the method; Hanna reagents are
  // per-param boxes.
  const paramWide = method === 'strip';

  const dismiss = () => {
    try {
      localStorage.setItem(reagentNudgeKey(tank.id, method, param), '1');
    } catch {
      /* private mode — non-fatal */
    }
    onDone();
  };

  const track = async () => {
    const sizeN = Number(size);
    if (!(sizeN > 0) || busy) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/supplies`, {
        method: 'POST',
        body: {
          name: defaultName.slice(0, 100),
          category: 'test-reagent',
          unit: 'tests',
          size: sizeN,
          // The test that triggered this nudge already used one.
          remaining: Math.max(0, sizeN - 1),
          daily_use: 0,
          test_method: method,
          test_param: paramWide ? null : param,
        },
      });
      onDone();
    } catch (e) {
      setErr(e.message || 'Could not add the supply.');
      setBusy(false);
    }
  };

  const box = {
    padding: 14,
    borderRadius: 14,
    background: 'var(--accent-bg)',
    border: '1px solid var(--accent-border)',
  };

  return (
    <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={box}>
        <div
          className="font-heading"
          style={{
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
            marginBottom: 6,
          }}
        >
          Reading saved · Track the {method === 'strip' ? 'strips' : 'reagent'}?
        </div>
        <p
          style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)', margin: '0 0 10px' }}
        >
          That test used one {method === 'strip' ? 'strip' : 'reagent packet'}. Tell Sensei how many
          come in the package and every future check counts down automatically — you get a warning
          before the box runs dry.
        </p>
        <label style={{ display: 'block' }}>
          <span
            className="font-heading"
            style={{
              display: 'block',
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
              marginBottom: 3,
            }}
          >
            {defaultName} · tests per package
          </span>
          <input
            className="lv-input"
            type="number"
            inputMode="numeric"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--bg)',
              border: '1px solid var(--surface-border)',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 16,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </label>
        {err && (
          <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', margin: '8px 0 0' }}>
            {err}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={dismiss}
            disabled={busy}
            className="font-heading"
            style={{
              flex: 1,
              minHeight: 44,
              padding: '10px 0',
              borderRadius: 999,
              border: '1px solid var(--surface-border)',
              color: 'var(--text-muted)',
              fontSize: 13,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
          >
            Not now
          </button>
          <button
            onClick={track}
            disabled={busy || !(Number(size) > 0)}
            className="font-heading"
            style={{
              flex: 2,
              minHeight: 44,
              padding: '10px 0',
              borderRadius: 999,
              background: 'var(--cyan)',
              color: 'var(--cyan-text)',
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              opacity: busy || !(Number(size) > 0) ? 0.5 : 1,
            }}
          >
            {busy ? 'Adding…' : 'Track it'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Same moment-of-use capture for ICP kits: the import that just
// finished consumed one. Kits are bought a few at a time and the
// quarterly retest sneaks up — one number and Sensei counts the rest.
function TrackIcpKitNudge({ tank, lab, onDone }) {
  const [count, setCount] = useState('1');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const dismiss = () => {
    try {
      localStorage.setItem(reagentNudgeKey(tank.id, 'icp', 'kit'), '1');
    } catch {
      /* private mode — non-fatal */
    }
    onDone();
  };

  const track = async () => {
    const n = Number(count);
    if (!(n >= 0) || busy) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/supplies`, {
        method: 'POST',
        body: {
          name: `${lab} ICP kit`,
          category: 'icp-kit',
          unit: 'kits',
          size: Math.max(1, n),
          remaining: n,
          daily_use: 0,
        },
      });
      onDone();
    } catch (e) {
      setErr(e.message || 'Could not add the supply.');
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '0 20px 120px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          padding: 14,
          borderRadius: 14,
          background: 'var(--accent-bg)',
          border: '1px solid var(--accent-border)',
        }}
      >
        <div
          className="font-heading"
          style={{
            fontSize: 12,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
            marginBottom: 6,
          }}
        >
          Report imported · Track your ICP kits?
        </div>
        <p
          style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)', margin: '0 0 10px' }}
        >
          That import used one kit. Tell Sensei how many you still have on the shelf and every
          future import counts them down — no more finding out at retest time that the box is empty.
        </p>
        <label style={{ display: 'block' }}>
          <span
            className="font-heading"
            style={{
              display: 'block',
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
              marginBottom: 3,
            }}
          >
            Kits on hand now
          </span>
          <input
            className="lv-input"
            type="number"
            inputMode="numeric"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--bg)',
              border: '1px solid var(--surface-border)',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 16,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </label>
        {err && (
          <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', margin: '8px 0 0' }}>
            {err}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={dismiss}
            disabled={busy}
            className="font-heading"
            style={{
              flex: 1,
              minHeight: 44,
              padding: '10px 0',
              borderRadius: 999,
              border: '1px solid var(--surface-border)',
              color: 'var(--text-muted)',
              fontSize: 13,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
          >
            Not now
          </button>
          <button
            onClick={track}
            disabled={busy || !(Number(count) >= 0)}
            className="font-heading"
            style={{
              flex: 2,
              minHeight: 44,
              padding: '10px 0',
              borderRadius: 999,
              background: 'var(--cyan)',
              color: 'var(--cyan-text)',
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              opacity: busy || !(Number(count) >= 0) ? 0.5 : 1,
            }}
          >
            {busy ? 'Adding…' : 'Track them'}
          </button>
        </div>
      </div>
    </div>
  );
}
